// src/Dashboard/pages/user/TechniquesQuizzDashboard/TechniquesQuizzDashboard.jsx
// Adapte le chemin si ton dossier est différent.

import React, { useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useTechniquesQuizzDashboard } from "./hooks/useTechniquesQuizzDashboard";
import { useTechniquesMemoryGame } from "./hooks/useTechniquesMemoryGame";

import TechniquesQuizzDashboardHeader from "./components/TechniquesQuizzDashboardHeader";
import TechniquesGameModeTabs from "./components/TechniquesGameModeTabs";

import TechniquesQuizzSettingsCard from "./components/TechniquesQuizzSettingsCard";
import TechniquesQuizzKpiCards from "./components/TechniquesQuizzKpiCards";
import QuizzProgressBar from "./components/QuizzProgressBar";
import QuizzQuestionCard from "./components/QuizzQuestionCard";
import QuizzResultsPanel from "./components/QuizzResultsPanel";
import QuizzLoadingState from "./components/QuizzLoadingState";
import QuizzEmptyState from "./components/QuizzEmptyState";

import TechniquesMemorySettingsCard from "./components/TechniquesMemorySettingsCard";
import TechniquesMemoryGamePanel from "./components/TechniquesMemoryGamePanel";

export default function TechniquesQuizzDashboard() {
  useScrollToTop();

  const [gameMode, setGameMode] = useState("quizz");

  const {
    loading,
    techniques,
    questions,
    currentQuestion,
    current,
    selected,
    score,
    errors,
    finished,
    progress,
    stats,

    ui,
    dynamicOptions,
    updateUi,

    answerQuestion,
    nextQuestion,
    restart,
  } = useTechniquesQuizzDashboard();

  const memoryGame = useTechniquesMemoryGame({
    techniques,
    loading,
  });

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <TechniquesQuizzDashboardHeader />

        <TechniquesGameModeTabs
          value={gameMode}
          onChange={setGameMode}
          quizzStats={stats}
          memoryStats={memoryGame.stats}
        />

        {gameMode === "quizz" ? (
          <>
            {!finished && (
              <TechniquesQuizzSettingsCard
                ui={ui}
                dynamicOptions={dynamicOptions}
                loading={loading}
                totalTechniques={techniques.length}
                onChange={updateUi}
                onRestart={restart}
              />
            )}

            <TechniquesQuizzKpiCards stats={stats} loading={loading} />

            <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
              <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

              <div className="p-4 sm:p-5 lg:p-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
                      Quizz techniques
                    </p>

                    <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
                      Trouver le nom de la technique
                    </h2>

                    <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
                      Observez l’image, choisissez la bonne réponse, puis passez
                      à la question suivante. Les réponses sont mélangées à
                      chaque nouvelle partie.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800/60">
                    <span className="text-slate-500 dark:text-slate-400">
                      Question :
                    </span>{" "}
                    <span className="font-bold text-slate-900 dark:text-white">
                      {questions.length > 0 ? current + 1 : 0}
                    </span>
                    <span className="text-slate-500 dark:text-slate-400">
                      {" "}
                      / {questions.length}
                    </span>
                  </div>
                </div>

                <div className="mt-5">
                  {loading ? (
                    <QuizzLoadingState />
                  ) : finished ? (
                    <QuizzResultsPanel
                      questions={questions}
                      errors={errors}
                      score={score}
                      onRestart={restart}
                    />
                  ) : questions.length === 0 ? (
                    <QuizzEmptyState onRestart={restart} />
                  ) : (
                    <>
                      <div className="mb-5">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <p className="text-sm font-bold text-slate-900 dark:text-white">
                            Question {current + 1} sur {questions.length}
                          </p>

                          <p className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                            Score : {score}
                          </p>
                        </div>

                        <QuizzProgressBar value={progress} />
                      </div>

                      <QuizzQuestionCard
                        question={currentQuestion}
                        selected={selected}
                        onAnswer={answerQuestion}
                        onNext={nextQuestion}
                        hasNext={current < questions.length - 1}
                      />
                    </>
                  )}
                </div>
              </div>
            </section>
          </>
        ) : (
          <>
            <TechniquesMemorySettingsCard
              ui={memoryGame.ui}
              loading={loading}
              grades={memoryGame.gradeOptions}
              cardCountOptions={memoryGame.cardCountOptions}
              availableTechniques={memoryGame.availableTechniques.length}
              includeSelfDefense={ui.includeSelfDefense}
              onIncludeSelfDefenseChange={(value) =>
                updateUi({ includeSelfDefense: value })
              }
              onChange={memoryGame.updateUi}
              onRestart={memoryGame.restart}
            />

            <TechniquesMemoryGamePanel
              loading={loading}
              cards={memoryGame.cards}
              flippedIds={memoryGame.flippedIds}
              matchedIds={memoryGame.matchedIds}
              locked={memoryGame.locked}
              stats={memoryGame.stats}
              onCardClick={memoryGame.flipCard}
              onRestart={memoryGame.restart}
            />
          </>
        )}
      </div>

      <ToastContainer />
    </section>
  );
}